import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { Search as SearchIcon, Groups as GroupsIcon } from '@mui/icons-material'
import { userAtom } from '../state/auth'
import {
  rememberedClientIdAtom,
  scopeJobSearchClients,
  workflowStageDefinitionsAtom,
} from '../state/clients'
import { isAdminOrManager } from '../utils/roles'
import { clientsApi, jobOpportunitiesApi, Client } from '../services/api'
import type { JobOpportunity } from '../state/jobOpportunities'
import { isFrontDoorJob } from '../components/job-applications/jobTabRouting'
import SortableTableHeader, { SortOrder } from '../components/common/table/SortableTableHeader'
import { useTablePagination } from '../components/common/table/useTablePagination'
import TablePaginationBar from '../components/common/table/TablePaginationBar'
import { getEnhancedTableStyles } from '../utils/tableStyles'

type SortKey = 'name' | 'stage' | 'applications' | 'updated'

/**
 * The client chooser for the two Job Search surfaces.
 *
 * Both live at /clients/:id/..., so the bare /opportunities and /networking
 * paths need to answer "which client". They used to answer it themselves, by
 * redirecting to the remembered client or the first market-ready one — which
 * meant there was no way to see the list and pick, and on a narrow viewport no
 * way to change client at all, because the navbar switcher is hidden below sm.
 *
 * An explicit ?clientId= still redirects, for old bookmarks and external links;
 * nothing in the app builds that query for these paths any more. Only the
 * un-parameterised route lands here.
 */
export default function JobSearchClientIndex({
  page,
}: {
  page: 'job-applications' | 'networking'
}) {
  const theme = useTheme()
  const tableStyles = getEnhancedTableStyles(theme)
  const navigate = useNavigate()
  const [user] = useAtom(userAtom)
  const [remembered] = useAtom(rememberedClientIdAtom)
  const [stageDefinitions] = useAtom(workflowStageDefinitionsAtom)

  const [clients, setClients] = useState<Client[]>([])
  const [jobs, setJobs] = useState<JobOpportunity[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [search, setSearch] = useState('')
  const [orderBy, setOrderBy] = useState<SortKey>('name')
  const [order, setOrder] = useState<SortOrder>('asc')

  // The applications column only exists on the job-applications index, so the
  // extra list call only happens there. It is the same call the destination
  // page makes on arrival, so this costs nothing the user was not about to pay.
  const needsJobCounts = page === 'job-applications'

  // The live request. Incremented per load so a superseded one — an unmount, or
  // a second Retry click — cannot write its result over a newer one.
  const loadIdRef = useRef(0)

  const load = useCallback(() => {
    const loadId = ++loadIdRef.current
    setLoading(true)
    setError(false)
    Promise.all([
      clientsApi.list({ all: true }),
      needsJobCounts ? jobOpportunitiesApi.list({ all: true }) : Promise.resolve([]),
    ])
      .then(([clientResult, jobResult]) => {
        if (loadId !== loadIdRef.current) return
        setClients(Array.isArray(clientResult) ? clientResult : [])
        setJobs(Array.isArray(jobResult) ? jobResult : [])
        setError(false)
      })
      .catch((err) => {
        if (loadId !== loadIdRef.current) return
        console.error('Failed to load the Job Search client index:', err)
        setError(true)
      })
      .finally(() => {
        if (loadId !== loadIdRef.current) return
        setLoading(false)
      })
  }, [needsJobCounts])

  useEffect(() => {
    load()
    return () => {
      // Abandons the in-flight load rather than letting it settle into a tree
      // that is going away.
      loadIdRef.current += 1
    }
  }, [load])

  const stageLabels = useMemo(
    () => Object.fromEntries((stageDefinitions || []).map((s) => [s.stageId, s.label])),
    [stageDefinitions]
  )

  const applicationCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const job of jobs) {
      if (!job?.clientId || job.isDeleted || job.archived) continue
      // MWA-421 — a signal is not on the Front Door, so it is not one of its applications.
      if (!isFrontDoorJob(job)) continue
      counts[job.clientId] = (counts[job.clientId] || 0) + 1
    }
    return counts
  }, [jobs])

  const isManager = isAdminOrManager(user) || user?.role === 'client_success'

  // The same scoping the navbar switcher uses, from one shared helper, so the
  // two can never offer different sets for the same route.
  const scoped = useMemo(
    () => scopeJobSearchClients({ clients, userEmail: user?.email, isManager, subPath: page }),
    [clients, user?.email, isManager, page]
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return scoped
    return scoped.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q)
    )
  }, [scoped, search])

  const sorted = useMemo(() => {
    const dir = order === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      switch (orderBy) {
        case 'stage':
          return (
            dir *
            (stageLabels[a.workflowStage] || a.workflowStage || '').localeCompare(
              stageLabels[b.workflowStage] || b.workflowStage || ''
            )
          )
        case 'applications':
          return dir * ((applicationCounts[a.id] || 0) - (applicationCounts[b.id] || 0))
        case 'updated':
          return (
            dir *
            ((Date.parse(a.updatedAt || '') || 0) - (Date.parse(b.updatedAt || '') || 0))
          )
        default:
          return dir * (a.name || a.email || '').localeCompare(b.name || b.email || '')
      }
    })
  }, [filtered, orderBy, order, stageLabels, applicationCounts])

  const { paginated, ...pageCtl } = useTablePagination(sorted, `${orderBy}|${order}|${search}`)

  const handleSort = (columnId: string) => {
    const key = columnId as SortKey
    if (key === orderBy) {
      setOrder(order === 'asc' ? 'desc' : 'asc')
      return
    }
    setOrderBy(key)
    // Names read naturally A-Z; counts and dates are asked about newest/most
    // first, so they open descending.
    setOrder(key === 'name' || key === 'stage' ? 'asc' : 'desc')
  }

  const title = page === 'job-applications' ? 'Front Door' : 'Side Door'

  return (
    <Box>
      <Box sx={{ px: 2, pt: 2 }}>
        {/* The theme's h4 line height is a fixed rem sized for its own 1.25rem, so
            the larger font needs a line height of its own or its glyphs spill
            into the subtitle below. */}
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, lineHeight: 1.3 }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ px: 2, pb: 1 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Choose a client to open their {page === 'job-applications' ? 'applications' : 'network'}.
        </Typography>

        {!loading && !error && scoped.length > 0 && (
          <TextField
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{ maxWidth: 360 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Box>

      <Box sx={{ p: 2, pt: 1 }}>
        {loading ? (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={52} />
            <Skeleton variant="rounded" height={52} />
            <Skeleton variant="rounded" height={52} />
            <Skeleton variant="rounded" height={52} />
          </Stack>
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={load}>
                Retry
              </Button>
            }
          >
            We could not load your clients. Try again, and if this continues tell an admin.
          </Alert>
        ) : scoped.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <GroupsIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              {page === 'job-applications'
                ? 'No market-ready clients yet. Go-To-Market opens once a client reaches the Market Ready stage.'
                : 'No clients assigned to you yet.'}
            </Typography>
          </Paper>
        ) : sorted.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No client matches &ldquo;{search.trim()}&rdquo;.
            </Typography>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={tableStyles.tableContainer}>
            <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
              <Table stickyHeader sx={{ ...tableStyles.table, minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <SortableTableHeader
                      id="name"
                      label="Client"
                      orderBy={orderBy}
                      order={order}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      id="stage"
                      label="Stage"
                      orderBy={orderBy}
                      order={order}
                      onSort={handleSort}
                    />
                    {needsJobCounts && (
                      <SortableTableHeader
                        id="applications"
                        label="Applications"
                        orderBy={orderBy}
                        order={order}
                        onSort={handleSort}
                        align="right"
                      />
                    )}
                    <SortableTableHeader
                      id="updated"
                      label="Last updated"
                      orderBy={orderBy}
                      order={order}
                      onSort={handleSort}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((c) => (
                    <TableRow
                      key={c.id}
                      hover
                      onClick={() => navigate(`/clients/${c.id}/${page}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          useFlexGap
                          sx={{ flexWrap: 'wrap' }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {c.name || c.email}
                          </Typography>
                          {/* The route used to open this client automatically. It
                              is marked rather than jumped to, so returning here
                              is still one click without the list being skipped. */}
                          {c.id === remembered && (
                            <Chip label="Last opened" size="small" variant="outlined" />
                          )}
                        </Stack>
                        {c.name && c.email && (
                          <Typography variant="caption" color="text.secondary">
                            {c.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {stageLabels[c.workflowStage] || c.workflowStage || '—'}
                        </Typography>
                      </TableCell>
                      {needsJobCounts && (
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {applicationCounts[c.id] || 0}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {c.updatedAt ? new Date(c.updatedAt).toLocaleDateString() : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <TablePaginationBar count={pageCtl.count} page={pageCtl.page} rowsPerPage={pageCtl.rowsPerPage} setPage={pageCtl.setPage} setRowsPerPage={pageCtl.setRowsPerPage} />
          </Paper>
        )}
      </Box>
    </Box>
  )
}
